/**
 * ScriptWindow Component
 *
 * Displays the script/screenplay for all beats in sequence.
 * The view auto-scrolls to keep the current beat's script visible.
 *
 * SCREENPLAY FORMATTING:
 * ======================
 * We use a simplified Fountain-style formatting:
 * - Scene headings (INT./EXT.) are bold and uppercase
 * - Character names (all caps lines before dialogue) are centered
 * - Dialogue is indented
 * - Action lines are normal text
 *
 * The script is rendered as Markdown with additional screenplay styling.
 */

import { useMemo, useEffect, useRef, useState, CSSProperties } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import { useCurrentTime } from '@/stores/playbackStore';
import { TimelineBeat } from '@/types';
import { parseScreenplay } from '@/utils/fountainParser';

/**
 * ScriptWindow Component
 */
function ScriptWindow() {
  // ---------------------------------------------------------------------------
  // REFS
  // ---------------------------------------------------------------------------

  const containerRef = useRef<HTMLDivElement>(null);
  const beatRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // ---------------------------------------------------------------------------
  // STORE ACCESS
  // ---------------------------------------------------------------------------

  // Subscribe to nodes directly for reactivity (not getSortedNodes function)
  const nodes = useProjectStore((state) => state.nodes);
  const updateNode = useProjectStore((state) => state.updateNode);
  const selectNode = useProjectStore((state) => state.selectNode);
  const currentTime = useCurrentTime();

  // Track which beat is being edited (nodeId -> true)
  const [editingBeatId, setEditingBeatId] = useState<string | null>(null);
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [editingSceneHeadingId, setEditingSceneHeadingId] = useState<string | null>(null);
  const [localScriptValue, setLocalScriptValue] = useState<string>('');
  const [localTitleValue, setLocalTitleValue] = useState<string>('');

  // Track if ANY field is being edited (to disable auto-scroll)
  const isAnyFieldBeingEdited = editingBeatId !== null ||
                                 editingTitleId !== null ||
                                 editingSceneHeadingId !== null;

  // ---------------------------------------------------------------------------
  // CALCULATE TIMELINE BEATS
  // ---------------------------------------------------------------------------

  const timelineBeats: TimelineBeat[] = useMemo(() => {
    // Filter out nodes that are on the shelf, then sort by x-position for timeline order
    const filtered = nodes.filter((node) => !node.data.onShelf);
    const sorted = [...filtered].sort((a, b) => a.position.x - b.position.x);
    let currentStart = 0;

    return sorted.map((node, index) => {
      const startTime = currentStart;
      const endTime = startTime + node.data.duration;
      currentStart = endTime;

      return {
        node,
        startTime,
        endTime,
        index,
      };
    });
  }, [nodes]);

  // ---------------------------------------------------------------------------
  // FIND ACTIVE BEAT
  // ---------------------------------------------------------------------------

  const activeBeatIndex = useMemo(() => {
    const index = timelineBeats.findIndex(
      (b) => currentTime >= b.startTime && currentTime < b.endTime
    );

    // If at the end, return last beat
    if (index === -1 && timelineBeats.length > 0) {
      const lastBeat = timelineBeats[timelineBeats.length - 1];
      if (currentTime >= lastBeat.startTime) {
        return timelineBeats.length - 1;
      }
    }

    return index;
  }, [timelineBeats, currentTime]);

  // ---------------------------------------------------------------------------
  // AUTO-SCROLL BASED ON PLAYBACK POSITION
  // ---------------------------------------------------------------------------

  useEffect(() => {
    // Don't auto-scroll while user is editing
    if (isAnyFieldBeingEdited) {
      return;
    }

    if (!containerRef.current) return;
    if (timelineBeats.length === 0) return;

    // Get total timeline duration
    const totalDuration = timelineBeats[timelineBeats.length - 1]?.endTime || 0;
    if (totalDuration === 0) return;

    // Calculate the overall progress through the entire timeline (0 to 1)
    const overallProgress = Math.min(1, Math.max(0, currentTime / totalDuration));

    // Get total scrollable height
    const scrollHeight = containerRef.current.scrollHeight;
    const containerHeight = containerRef.current.clientHeight;
    const maxScroll = scrollHeight - containerHeight;

    // Calculate target scroll position based on overall timeline progress
    const targetScrollTop = maxScroll * overallProgress;

    // Center the position in the viewport
    const centeredScrollTop = targetScrollTop;

    // Smooth scroll to the calculated position
    containerRef.current.scrollTo({
      top: Math.max(0, centeredScrollTop),
      behavior: 'smooth',
    });
  }, [timelineBeats, currentTime, isAnyFieldBeingEdited]);

  // ---------------------------------------------------------------------------
  // RENDER BEAT CONTENT
  // ---------------------------------------------------------------------------

  /**
   * Render a single beat's script with screenplay formatting
   */
  const renderBeatScript = (beat: TimelineBeat, isActive: boolean) => {
    const isEditing = editingBeatId === beat.node.id;
    const lines = parseScreenplay(beat.node.data.script || '');

    const handleClick = () => {
      // Save current script edit if we're clicking away from an editing beat
      if (editingBeatId && editingBeatId !== beat.node.id) {
        const currentlyEditingBeat = timelineBeats.find(b => b.node.id === editingBeatId);
        if (currentlyEditingBeat && localScriptValue !== currentlyEditingBeat.node.data.script) {
          updateNode(editingBeatId, { script: localScriptValue });
        }
        // Reset local script value after saving
        setLocalScriptValue('');
      }

      // Save current title edit if we're clicking away from an editing title
      if (editingTitleId && editingTitleId !== beat.node.id) {
        const currentlyEditingTitleBeat = timelineBeats.find(b => b.node.id === editingTitleId);
        if (currentlyEditingTitleBeat && localTitleValue !== currentlyEditingTitleBeat.node.data.title) {
          updateNode(editingTitleId, { title: localTitleValue });
        }
        // Reset local title value after saving
        setLocalTitleValue('');
      }

      if (!isEditing) {
        setEditingBeatId(beat.node.id);
        // Initialize local script value when starting to edit
        setLocalScriptValue(beat.node.data.script || '');
        selectNode(beat.node.id); // Select beat to show properties
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setLocalScriptValue(e.target.value);
    };

    const handleBlur = () => {
      // Save on blur when clicking outside
      if (localScriptValue !== beat.node.data.script) {
        updateNode(beat.node.id, { script: localScriptValue });
      }
      setEditingBeatId(null);
      setLocalScriptValue('');
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Exit edit mode on Escape - save immediately and don't trigger blur
      if (e.key === 'Escape') {
        // Save immediately to ensure the value is preserved
        if (localScriptValue !== beat.node.data.script) {
          updateNode(beat.node.id, { script: localScriptValue });
        }
        // Exit edit mode WITHOUT calling blur to prevent race conditions
        setEditingBeatId(null);
        setLocalScriptValue('');
        e.preventDefault(); // Prevent default behavior
        return;
      }
      // Allow Tab key to insert tab character
      if (e.key === 'Tab') {
        e.preventDefault();
        const target = e.target as HTMLTextAreaElement;
        const start = target.selectionStart;
        const end = target.selectionEnd;
        const value = target.value;
        target.value = value.substring(0, start) + '\t' + value.substring(end);
        target.selectionStart = target.selectionEnd = start + 1;
        setLocalScriptValue(target.value);
      }
    };

    return (
      <div
        key={beat.node.id}
        ref={(el) => {
          if (el) beatRefs.current.set(beat.node.id, el);
        }}
        style={{
          ...beatContainerStyles,
          backgroundColor: isActive ? 'var(--nb-bg-active)' : 'transparent',
          borderLeftColor: isActive
            ? 'var(--nb-accent-primary)'
            : 'var(--nb-border-primary)',
        }}
        title={
          isActive
            ? `Currently playing: ${beat.node.data.title}`
            : `Beat ${beat.index + 1}: ${beat.node.data.title}`
        }
      >
        <div style={beatHeaderStyles}>
          {editingTitleId === beat.node.id ? (
            <input
              type="text"
              autoFocus
              value={localTitleValue}
              onChange={(e) => setLocalTitleValue(e.target.value)}
              onBlur={() => {
                // Save on blur (whether from clicking away or Escape key)
                if (localTitleValue !== beat.node.data.title) {
                  updateNode(beat.node.id, { title: localTitleValue });
                }
                setEditingTitleId(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  // Save immediately on Escape to prevent race conditions
                  if (localTitleValue !== beat.node.data.title) {
                    updateNode(beat.node.id, { title: localTitleValue });
                  }
                  // Exit edit mode WITHOUT calling blur
                  setEditingTitleId(null);
                  setLocalTitleValue('');
                  e.preventDefault();
                  return;
                }
                if (e.key === 'Enter') {
                  // For Enter, use blur to keep consistent with clicking away
                  e.currentTarget.blur();
                }
              }}
              style={titleInputStyles}
              placeholder="Beat title..."
              title="Edit beat title - press Enter or Escape to finish"
            />
          ) : (
            <span
              style={{...beatTitleStyles, cursor: 'pointer'}}
              onClick={() => {
                // Save current title edit if we're clicking away from an editing title
                if (editingTitleId && editingTitleId !== beat.node.id) {
                  const currentlyEditingTitleBeat = timelineBeats.find(b => b.node.id === editingTitleId);
                  if (currentlyEditingTitleBeat && localTitleValue !== currentlyEditingTitleBeat.node.data.title) {
                    updateNode(editingTitleId, { title: localTitleValue });
                  }
                }

                setEditingTitleId(beat.node.id);
                setLocalTitleValue(beat.node.data.title);
                selectNode(beat.node.id); // Select beat to show properties
              }}
              title={`Click to edit: ${beat.node.data.title}`}
            >
              {beat.node.data.title}
            </span>
          )}
          <span
            style={beatNumberStyles}
            title={`This is beat number ${beat.index + 1} in the timeline sequence`}
          >
            Beat {beat.index + 1}
          </span>
        </div>

        {/* Scene Heading - editable inline */}
        {(beat.node.data.sceneHeading || editingSceneHeadingId === beat.node.id) && (
          <div style={{ marginBottom: 'var(--nb-space-sm)', paddingTop: 'var(--nb-space-xs)' }}>
            {editingSceneHeadingId === beat.node.id ? (
              <input
                type="text"
                autoFocus
                value={beat.node.data.sceneHeading || ''}
                onChange={(e) => {
                  // Update immediately for title and scene heading (they're short text)
                  updateNode(beat.node.id, { sceneHeading: e.target.value });
                }}
                onBlur={() => setEditingSceneHeadingId(null)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    setEditingSceneHeadingId(null);
                    e.currentTarget.blur();
                  }
                }}
                style={sceneHeadingInputStyles}
                placeholder="INT./EXT. LOCATION - TIME"
                title="Edit scene heading - press Enter or Escape to finish"
              />
            ) : (
              <div
                style={{...sceneHeadingStyles, cursor: 'pointer'}}
                onClick={() => {
                  setEditingSceneHeadingId(beat.node.id);
                  selectNode(beat.node.id); // Select beat to show properties
                }}
                title={`Click to edit scene heading: ${beat.node.data.sceneHeading || 'Add scene heading'}`}
              >
                {beat.node.data.sceneHeading || (
                  <span style={{ color: 'var(--nb-text-muted)', fontStyle: 'italic', fontWeight: 'normal', textTransform: 'none' }}>
                    Click to add scene heading
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {isEditing ? (
          <textarea
            autoFocus
            value={localScriptValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            style={textareaStyles}
            placeholder="Enter script text..."
            title="Edit script content - press Escape to finish, or click anywhere outside"
          />
        ) : (
          <div
            style={{
              ...scriptContentStyles,
              cursor: 'pointer',
            }}
            onClick={handleClick}
            title="Click to edit this script. Write action descriptions, dialogue, and other screenplay content."
          >
            {lines.length > 0 ? (
              lines.map((line, i) => (
                <div key={i} style={getLineStyle(line.type)}>
                  {line.text}
                </div>
              ))
            ) : (
              <div style={emptyScriptStyles} title="Click to add action descriptions, dialogue, or other script content">
                Click to add script content
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // STYLES
  // ---------------------------------------------------------------------------

  const containerStyles: CSSProperties = {
    flex: 1,
    overflow: 'auto',
    backgroundColor: 'var(--nb-bg-secondary)',
    padding: 'var(--nb-space-sm)',
  };

  const beatContainerStyles: CSSProperties = {
    marginBottom: 'var(--nb-space-md)',
    padding: 'var(--nb-space-sm)',
    borderLeft: '3px solid',
    borderRadius: 'var(--nb-radius-sm)',
    transition: 'background-color var(--nb-transition-normal)',
  };

  const beatHeaderStyles: CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 'var(--nb-space-xs)',
    paddingBottom: 'var(--nb-space-xs)',
    borderBottom: '1px solid var(--nb-border-primary)',
  };

  const beatTitleStyles: CSSProperties = {
    fontWeight: 600,
    fontSize: 'var(--nb-font-size-sm)',
    color: 'var(--nb-text-primary)',
  };

  const beatNumberStyles: CSSProperties = {
    fontSize: 'var(--nb-font-size-xs)',
    color: 'var(--nb-text-muted)',
  };

  const sceneHeadingStyles: CSSProperties = {
    fontFamily: 'var(--nb-font-mono)',
    fontSize: 'var(--nb-font-size-sm)',
    fontWeight: 700,
    textTransform: 'uppercase',
    color: 'var(--nb-text-primary)',
    marginBottom: 'var(--nb-space-sm)',
    paddingTop: 'var(--nb-space-xs)',
  };

  const scriptContentStyles: CSSProperties = {
    fontFamily: 'var(--nb-font-mono)',
    fontSize: 'var(--nb-font-size-sm)',
    lineHeight: 1.6,
  };

  const emptyScriptStyles: CSSProperties = {
    color: 'var(--nb-text-muted)',
    fontStyle: 'italic',
  };

  const textareaStyles: CSSProperties = {
    width: '100%',
    minHeight: '150px',
    fontFamily: 'var(--nb-font-mono)',
    fontSize: 'var(--nb-font-size-sm)',
    lineHeight: 1.6,
    padding: 'var(--nb-space-sm)',
    backgroundColor: 'var(--nb-bg-primary)',
    color: 'var(--nb-text-primary)',
    border: '1px solid var(--nb-accent-primary)',
    borderRadius: 'var(--nb-radius-sm)',
    resize: 'vertical',
    outline: 'none',
  };

  const titleInputStyles: CSSProperties = {
    flex: 1,
    fontWeight: 600,
    fontSize: 'var(--nb-font-size-sm)',
    color: 'var(--nb-text-primary)',
    backgroundColor: 'var(--nb-bg-primary)',
    border: '1px solid var(--nb-accent-primary)',
    borderRadius: 'var(--nb-radius-sm)',
    padding: '2px 6px',
    outline: 'none',
    marginRight: 'var(--nb-space-sm)',
  };

  const sceneHeadingInputStyles: CSSProperties = {
    width: '100%',
    fontFamily: 'var(--nb-font-mono)',
    fontSize: 'var(--nb-font-size-sm)',
    fontWeight: 700,
    textTransform: 'uppercase',
    color: 'var(--nb-text-primary)',
    backgroundColor: 'var(--nb-bg-primary)',
    border: '1px solid var(--nb-accent-primary)',
    borderRadius: 'var(--nb-radius-sm)',
    padding: '4px 8px',
    outline: 'none',
  };

  /**
   * Get style for a screenplay line based on its type
   */
  const getLineStyle = (type: string): CSSProperties => {
    const baseStyle: CSSProperties = {
      marginBottom: 'var(--nb-space-xs)',
    };

    switch (type) {
      case 'scene-heading':
        return {
          ...baseStyle,
          fontWeight: 700,
          textTransform: 'uppercase',
          color: 'var(--nb-text-primary)',
          marginTop: 'var(--nb-space-sm)',
        };

      case 'character':
        return {
          ...baseStyle,
          textTransform: 'uppercase',
          textAlign: 'center',
          marginTop: 'var(--nb-space-sm)',
          color: 'var(--nb-text-primary)',
        };

      case 'dialogue':
        return {
          ...baseStyle,
          marginLeft: '20%',
          marginRight: '20%',
          color: 'var(--nb-text-primary)',
        };

      case 'parenthetical':
        return {
          ...baseStyle,
          marginLeft: '25%',
          marginRight: '25%',
          fontStyle: 'italic',
          color: 'var(--nb-text-secondary)',
        };

      case 'transition':
        return {
          ...baseStyle,
          textAlign: 'right',
          textTransform: 'uppercase',
          color: 'var(--nb-text-secondary)',
        };

      case 'action':
      default:
        return {
          ...baseStyle,
          color: 'var(--nb-text-primary)',
        };
    }
  };

  const emptyStateStyles: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: 'var(--nb-text-muted)',
    textAlign: 'center',
    padding: 'var(--nb-space-md)',
  };

  // ---------------------------------------------------------------------------
  // SYNC LOCAL STATE WITH GLOBAL STATE
  // ---------------------------------------------------------------------------

  // No local state sync needed - we now use global state directly

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  if (timelineBeats.length === 0) {
    return (
      <div style={containerStyles}>
        <div
          style={emptyStateStyles}
          title="Create beats in the Node View to see your script here"
        >
          <p>No beats to display.<br />Create beats in the Node View.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={containerStyles}
      title="Your screenplay script - shows all beats in sequence. Click any script to edit. Auto-scrolls to follow playback."
    >
      {timelineBeats.map((beat, index) =>
        renderBeatScript(beat, index === activeBeatIndex)
      )}
    </div>
  );
}

export default ScriptWindow;
